import { useNavigate } from "@remix-run/react";
import { CloudUpload, X } from "lucide-react";
import { type ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getUploadExperienceCopy } from "../../data/uploadExperienceCopy";
import { useTranslation } from "../../i18n";
import { uploadFileError } from "../../utils/uploadFile";
import "./upload-experience.css";

type Receiver = { select: (file: File) => void; busy: boolean };
type DropContext = {
	register: (receiver: Receiver) => () => void;
	busy: (key: symbol, active: boolean) => void;
};
const UploadDropContext = createContext<DropContext | null>(null);

export function GlobalUploadDrop({ children }: { children: ReactNode }) {
	const navigate = useNavigate();
	const { locale, t, localizedPath } = useTranslation();
	const copy = getUploadExperienceCopy(locale);
	const receiver = useRef<Receiver | null>(null);
	const pending = useRef<File | null>(null);
	const activeUploads = useRef(new Set<symbol>());
	const [dragging, setDragging] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);
	const register = useCallback((value: Receiver) => {
		receiver.current = value;
		if (pending.current) {
			const file = pending.current;
			pending.current = null;
			value.select(file);
		}
		return () => {
			if (receiver.current === value) receiver.current = null;
		};
	}, []);
	const busy = useCallback((key: symbol, active: boolean) => {
		if (active) activeUploads.current.add(key);
		else activeUploads.current.delete(key);
	}, []);
	useEffect(() => {
		const isFile = (event: DragEvent) => event.dataTransfer?.types.includes("Files");
		let depth = 0;
		const clear = () => {
			depth = 0;
			setDragging(false);
		};
		const enter = (event: DragEvent) => {
			if (!isFile(event)) return;
			event.preventDefault();
			depth++;
			setDragging(true);
		};
		const over = (event: DragEvent) => {
			if (!isFile(event)) return;
			event.preventDefault();
			if (event.dataTransfer)
				event.dataTransfer.dropEffect = activeUploads.current.size || receiver.current?.busy ? "none" : "copy";
			setDragging(true);
		};
		const leave = (event: DragEvent) => {
			if (!isFile(event)) return;
			depth = Math.max(0, depth - 1);
			if (!depth) clear();
		};
		const drop = (event: DragEvent) => {
			if (!isFile(event)) return;
			event.preventDefault();
			event.stopPropagation();
			clear();
			if (activeUploads.current.size || receiver.current?.busy) {
				setNotice(copy.busy);
				return;
			}
			const files = event.dataTransfer?.files;
			if (!files || files.length !== 1) {
				setNotice(copy.oneFile);
				return;
			}
			const file = files[0];
			const error = uploadFileError(file);
			if (error) {
				setNotice(t(error));
				return;
			}
			setNotice(null);
			if (receiver.current) receiver.current.select(file);
			else if (document.querySelector('[role="dialog"]')) setNotice(copy.closeEditor);
			else {
				pending.current = file;
				navigate(localizedPath("/"));
			}
		};
		const onEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") clear();
		};
		const visibility = () => {
			if (document.hidden) clear();
		};
		window.addEventListener("dragenter", enter, true);
		window.addEventListener("dragover", over, true);
		window.addEventListener("dragleave", leave, true);
		window.addEventListener("drop", drop, true);
		window.addEventListener("dragend", clear);
		window.addEventListener("blur", clear);
		window.addEventListener("keydown", onEscape);
		document.addEventListener("visibilitychange", visibility);
		return () => {
			window.removeEventListener("dragenter", enter, true);
			window.removeEventListener("dragover", over, true);
			window.removeEventListener("dragleave", leave, true);
			window.removeEventListener("drop", drop, true);
			window.removeEventListener("dragend", clear);
			window.removeEventListener("blur", clear);
			window.removeEventListener("keydown", onEscape);
			document.removeEventListener("visibilitychange", visibility);
		};
	}, [navigate, localizedPath, copy, t]);
	return (
		<UploadDropContext.Provider value={{ register, busy }}>
			{children}
			{(dragging || notice) &&
				createPortal(
					<>
						{dragging && (
							<div className="upload-drop-overlay" data-testid="global-upload-drop" aria-live="polite">
								<div className="upload-drop-frame">
									<div className="upload-drop-icon">
										<CloudUpload size={52} strokeWidth={1.5} />
									</div>
									<p className="upload-drop-label">UPLOAD</p>
									<strong>{activeUploads.current.size || receiver.current?.busy ? copy.busy : copy.release}</strong>
									<p>{copy.releaseHint}</p>
									<small>{copy.formats}</small>
								</div>
							</div>
						)}
						{notice && (
							<div className="upload-drop-notice" role="alert">
								<CloudUpload size={22} aria-hidden="true" />
								<p>{notice}</p>
								<button type="button" aria-label={copy.dismiss} onClick={() => setNotice(null)}>
									<X size={18} />
								</button>
							</div>
						)}
					</>,
					document.fullscreenElement ?? document.body,
				)}
		</UploadDropContext.Provider>
	);
}

export function useUploadDropReceiver(select: (file: File) => void, busy: boolean) {
	const context = useContext(UploadDropContext);
	const latest = useRef(select);
	latest.current = select;
	useEffect(() => context?.register({ select: file => latest.current(file), busy }), [context?.register, busy]);
}

export function useUploadDropBusy(active: boolean) {
	const context = useContext(UploadDropContext);
	const key = useRef(Symbol("upload"));
	useEffect(() => {
		context?.busy(key.current, active);
		return () => context?.busy(key.current, false);
	}, [context?.busy, active]);
}
